import { Link } from 'react-router-dom'
import { Building2, CircleDollarSign, Target } from 'lucide-react'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import type { FinanceRequestDetail } from '@/services/financeService'
import { formatFormFieldValue, formatScalarField, getFormFieldLabel } from './financeRequestFormDisplay'
import {
  FinanceDetailInfoRow,
  FinanceDetailSectionCard,
  FinanceDetailValueTile,
} from './FinanceDetailSectionCard'

type Props = {
  detail: FinanceRequestDetail
  canViewFinancialValues?: boolean
}

export function FinanceRequestClosedDealSummary({
  detail,
  canViewFinancialValues = true,
}: Props) {
  const formData =
    detail.formData && typeof detail.formData === 'object'
      ? (detail.formData as unknown as Record<string, unknown>)
      : {}
  const project = detail.project
  const empresa = project?.empresa
  const deal = project?.deal
  const showDealContext =
    detail.type === 'C4_CONTRATO_AGRADA' || Boolean(deal) || Boolean(empresa)

  if (!showDealContext) return null

  const valorTotal = formData.valorTotal
  const valorMensal = formData.valorMensal
  const valorSetup = formData.valorSetup

  return (
    <div className='space-y-4'>
      <div className='grid gap-4 lg:grid-cols-2'>
        <FinanceDetailSectionCard title='Cliente' icon={Building2} accent='#6366f1'>
          {empresa ? (
            <>
              <p className='mb-3 text-lg font-bold text-text-primary'>
                <Link
                  to={`/clientes/${empresa.id}?from=financeiro`}
                  className='transition-colors hover:text-accent'
                >
                  {empresa.nome}
                </Link>
              </p>
              <FinanceDetailInfoRow label='Razão social' value={empresa.razaoSocial ?? empresa.nome} />
              <FinanceDetailInfoRow label='CNPJ' value={empresa.cnpj ?? '—'} />
              {typeof formData.email === 'string' && formData.email && (
                <FinanceDetailInfoRow label='E-mail' value={formData.email} />
              )}
              {typeof formData.endereco === 'string' && formData.endereco && (
                <FinanceDetailInfoRow label='Endereço' value={formData.endereco} />
              )}
            </>
          ) : (
            <>
              <FinanceDetailInfoRow label='Razão social' value={formatScalarField(formData.razaoSocial)} />
              <FinanceDetailInfoRow label='CNPJ' value={formatScalarField(formData.cnpj)} />
            </>
          )}
        </FinanceDetailSectionCard>

        <FinanceDetailSectionCard title='Deal fechado' icon={Target} accent='#10b981'>
          {deal ? (
            <>
              <p className='mb-3 text-lg font-bold text-text-primary'>{deal.nome}</p>
              <FinanceDetailInfoRow label='Etapa' value={formatFinanceStatus(deal.etapa)} />
              {deal.dataFechamento && (
                <FinanceDetailInfoRow
                  label='Ganho em'
                  value={new Date(deal.dataFechamento).toLocaleDateString('pt-BR')}
                />
              )}
            </>
          ) : (
            <FinanceDetailInfoRow label='Resumo' value={formatScalarField(formData.summary)} />
          )}
          <FinanceDetailInfoRow label='Closer' value={formatScalarField(formData.closerNome)} />
          {typeof formData.closerEmail === 'string' && formData.closerEmail && (
            <FinanceDetailInfoRow label='E-mail closer' value={formData.closerEmail} />
          )}
        </FinanceDetailSectionCard>
      </div>

      {canViewFinancialValues && (
        <FinanceDetailSectionCard title='Valores e condições' icon={CircleDollarSign} accent='#ef4444'>
          <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
            {valorTotal != null && valorTotal !== '' && (
              <FinanceDetailValueTile
                label={getFormFieldLabel('valorTotal')}
                value={formatFormFieldValue('valorTotal', valorTotal)}
                accent='#ef4444'
                emphasized
              />
            )}
            {valorMensal != null && valorMensal !== '' && (
              <FinanceDetailValueTile
                label={getFormFieldLabel('valorMensal')}
                value={formatFormFieldValue('valorMensal', valorMensal)}
                accent='#6366f1'
              />
            )}
            {valorSetup != null && valorSetup !== '' && (
              <FinanceDetailValueTile
                label={getFormFieldLabel('valorSetup')}
                value={formatFormFieldValue('valorSetup', valorSetup)}
                accent='#f59e0b'
              />
            )}
            {formData.condicaoPagamento != null && formData.condicaoPagamento !== '' && (
              <FinanceDetailValueTile
                label={getFormFieldLabel('condicaoPagamento')}
                value={formatScalarField(formData.condicaoPagamento)}
                accent='#8b5cf6'
              />
            )}
          </div>
        </FinanceDetailSectionCard>
      )}
    </div>
  )
}
